import React, { useState, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { 
  X, Building2, IndianRupee, Calendar, ShieldCheck, Phone, 
  MapPin, TrendingUp, CheckCircle, ChevronLeft, ChevronRight 
} from 'lucide-react';

import b7_tower_cover from "../images/projects/b7_tower_cover.png";
import b1_a1 from "../images/projects/b1_a1.png";
import b1_a2 from "../images/projects/b1_a2.png";
import b1_a3 from "../images/projects/b1_a3.png";
import b1_a4 from "../images/projects/b1_a4.png";
import b1_a5 from "../images/projects/b1_a5.png";
import b1_a6 from "../images/projects/b1_a6.png";
import b1_a7 from "../images/projects/b1_a7.png";
import b1_a8 from "../images/projects/b1_a8.png";
import b1_a9 from "../images/projects/b1_a9.png";

const B7TowerMarketing = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // --- CAROUSEL STATE ---
  const carouselImages = [b1_a1, b1_a2, b1_a3, b1_a4, b1_a5, b1_a6, b1_a7, b1_a8, b1_a9];
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-play logic
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Changes image every 5 seconds

    return () => clearInterval(timer);
  }, [carouselImages.length]);

  const goToPrevious = () => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? carouselImages.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const isLastSlide = currentIndex === carouselImages.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };

  const goToSlide = (slideIndex) => {
    setCurrentIndex(slideIndex);
  };

  // --- DATA EXTRACTION (From PDF/Image) ---
  const paymentPlans = [
    { floor: 'Lower Ground', area: 3072.00, rate: 170000, total: 522240000, down: 130560000, q1: 97920000, q2: 97920000, q3: 97920000, q4: 97920000 },
    { floor: 'Ground Floor', area: 3510.00, rate: 220000, total: 772200000, down: 193050000, q1: 144787500, q2: 144787500, q3: 144787500, q4: 144787500 },
    { floor: '1st Floor', area: 2719.81, rate: 140000, total: 380773400, down: 95193350, q1: 71395013, q2: 71395013, q3: 71395013, q4: 71395013 },
    { floor: '2nd Floor', area: 2719.81, rate: 100000, total: 271981000, down: 67995250, q1: 50996438, q2: 50996438, q3: 50996438, q4: 50996438 },
    { floor: '3rd Floor', area: 2708.92, rate: 100000, total: 270892000, down: 67723000, q1: 50792250, q2: 50792250, q3: 50792250, q4: 50792250 },
    { floor: '4th Floor', area: 2708.92, rate: 100000, total: 270892000, down: 67723000, q1: 50792250, q2: 50792250, q3: 50792250, q4: 50792250 },
    { floor: '5th Floor', area: 2708.92, rate: 100000, total: 270892000, down: 67723000, q1: 50792250, q2: 50792250, q3: 50792250, q4: 50792250 },
    { floor: '6th Floor', area: 2708.92, rate: 100000, total: 270892000, down: 67723000, q1: 50792250, q2: 50792250, q3: 50792250, q4: 50792250 },
    { floor: '7th Floor', area: 2708.92, rate: 100000, total: 270892000, down: 67723000, q1: 50792250, q2: 50792250, q3: 50792250, q4: 50792250 },
    { floor: '8th Floor', area: 4583.81, rate: 170000, total: 779247700, down: 194811925, q1: 146108481, q2: 146108481, q3: 146108481, q4: 146108481 },
  ];

  const floorStats = [
    { floor: 'Lower Ground', gross: 3072, circulation: 1016, net: 2056 },
    { floor: 'Ground Floor', gross: 3510, circulation: 1067, net: 2443 },
    { floor: '1st Floor', gross: 2719.81, circulation: 173.81, net: 2546 },
    { floor: '2nd Floor', gross: 2719.81, circulation: 440.81, net: 2279 },
    { floor: '3rd Floor', gross: 2708.92, circulation: 411.92, net: 2297 },
    { floor: '4th Floor', gross: 2708.92, circulation: 411.92, net: 2279 },
    { floor: '5th Floor', gross: 2708.92, circulation: 411.92, net: 2297 },
    { floor: '6th Floor', gross: 2708.92, circulation: 411.92, net: 2279 },
    { floor: '7th Floor', gross: 2708.92, circulation: 411.92, net: 2297 },
    { floor: '8th Floor', gross: 2719.81, circulation: 440.81, net: 2279 },
    { floor: 'Top Roof Mumty', gross: 897.50, circulation: 897.50, net: 0 },
    { floor: 'Total', gross: 29183.53, circulation: 6095.53, net: 23088 },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/923487172565', '_blank');
  };

  // --- STRUCTURED DATA FOR SEO (JSON-LD) ---
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "name": "B7 Tower 1",
    "description": "Premium commercial office spaces available for sale at B7 Tower 1, located on Jinnah Avenue, Blue Area, Islamabad. Flexible 4-quarter installment plan available.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Plot # 112, Jinnah Avenue",
      "addressLocality": "Islamabad",
      "addressRegion": "ICT",
      "addressCountry": "PK"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "PKR",
      "price": "522240000",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 font-sans">
        
        {/* --- SEO HEAD --- */}
        <Helmet>
          <title>B7 Tower 1 | Premium Commercial Offices in Blue Area, Islamabad</title>
          <meta name="description" content="Invest in B7 Tower 1 on Jinnah Avenue. Premium commercial office spaces with flexible 25% down payment and 4 quarterly installments. Safe & secure investment in Islamabad." />
          <meta name="keywords" content="B7 Tower 1, Blue Area Islamabad, Commercial Offices, Real Estate Islamabad, Jinnah Avenue, Property Investment, Marketing & Developers, Office Space for Sale" />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://b7tower.com/" />
          <meta property="og:title" content="B7 Tower 1 | Premium Commercial Offices in Blue Area, Islamabad" />
          <meta property="og:description" content="B7 Tower 1 offers premium commercial office spaces in the heart of Islamabad. Secure your investment with a 25% down payment today." />
          <meta property="og:image" content={b7_tower_cover} />

          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content="https://b7tower.com/" />
          <meta property="twitter:title" content="B7 Tower 1 | Premium Commercial Offices in Blue Area, Islamabad" />
          <meta property="twitter:description" content="B7 Tower 1 offers premium commercial office spaces in the heart of Islamabad. Secure your investment with a 25% down payment today." />
          <meta property="twitter:image" content={b7_tower_cover} />
          
          {/* JSON-LD Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        </Helmet>

        {/* --- HERO SECTION --- */}
        <section className="relative overflow-hidden pt-10 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 z-10">
              <div>
                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                  <Building2 size={14} /> Premium Commercial Investment
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                  B7 <span className="text-yellow-500">TOWER 1</span>
                </h1>
                <p className="text-xl text-gray-600 mt-3 font-light tracking-wide">
                  From Vision to Value
                </p>
                <div className="flex items-center gap-2 mt-2 text-blue-700">
                  <MapPin size={18} />
                  <p className="font-medium text-sm">Plot # 112, Jinnah Avenue, Sector F7/G7, Blue Area, Islamabad</p>
                </div>
                <p className="text-gray-500 mt-4 max-w-lg">
                  Experience modern architecture with premium office spaces designed for success. 
                  Secure your stake in Islamabad's most prestigious commercial hub.
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={handleWhatsApp}
                  className="flex-1 sm:flex-none bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-green-500/30 transition transform hover:-translate-y-1 flex items-center justify-center gap-3"
                >
                  <Phone size={20} /> Book Now
                </button>
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="flex-1 sm:flex-none bg-white border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-blue-900/10 transition transform hover:-translate-y-1 flex items-center justify-center gap-3"
                >
                   View Payment Plan
                </button>
              </div>

              {/* Key Features */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-blue-100">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-900">25%</div>
                  <div className="text-xs text-gray-500">Down Payment</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-900">4</div>
                  <div className="text-xs text-gray-500">Quarterly Plans</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-900">29k</div>
                  <div className="text-xs text-gray-500">Sq. Ft. Gross</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-900">10</div>
                  <div className="text-xs text-gray-500">Premium Floors</div>
                </div>
              </div>
            </div>

            {/* Hero 3D Building Image */}
            <div className="relative lg:-mr-10">
              <div className="absolute -inset-4 bg-yellow-400/20 blur-3xl rounded-full"></div>
              <img 
                src={b7_tower_cover} 
                alt="B7 Tower 1 Architectural Design" 
                className="relative rounded-3xl shadow-2xl transform lg:rotate-2 hover:rotate-0 transition duration-500 border-4 border-white/50"
              />
            </div>
          </div>
        </section>

        {/* --- FLOOR STATS SECTION --- */}
        <section className="bg-white py-12 shadow-inner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-6 border-b border-gray-200 pb-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Building <span className="text-blue-600">Specifications</span></h2>
                <p className="text-gray-500 text-sm">Area breakdown per floor (Sq. Ft.)</p>
              </div>
              <div className="text-xs text-gray-400 flex gap-4">
                <span><span className="inline-block w-3 h-3 bg-green-400 rounded-full mr-1"></span> Net Area</span>
                <span><span className="inline-block w-3 h-3 bg-yellow-400 rounded-full mr-1"></span> Circulation</span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {floorStats.map((stat, index) => (
                <div key={index} className={`p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition ${index === floorStats.length - 1 ? 'bg-blue-900 text-white' : 'bg-slate-50'}`}>
                  <div className="font-bold text-sm mb-1">{stat.floor}</div>
                  <div className={`text-xs ${index === floorStats.length - 1 ? 'text-blue-200' : 'text-gray-500'}`}>Gross: <span className="font-mono font-bold">{stat.gross.toLocaleString()}</span></div>
                  <div className={`text-xs ${index === floorStats.length - 1 ? 'text-blue-200' : 'text-gray-500'}`}>Circ: <span className="font-mono">{stat.circulation.toLocaleString()}</span></div>
                  <div className={`text-xs font-bold mt-1 ${index === floorStats.length - 1 ? 'text-white' : 'text-green-600'}`}>Net: <span className="font-mono">{stat.net.toLocaleString()}</span></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- PAYMENT PLAN MODAL --- */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-300">
              <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-slate-50">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                    <Calendar className="text-blue-600" /> Investment Plan <span className="text-sm font-normal text-gray-500 ml-2">(25% Down + 4 Quarters)</span>
                  </h2>
                  <p className="text-sm text-gray-500">B7 Tower 1 - Premium Commercial Investment</p>
                </div>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 rounded-full hover:bg-red-100 text-red-500 transition"
                  aria-label="Close modal"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="overflow-auto flex-1 p-6">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-blue-900 text-white sticky top-0">
                    <tr>
                      <th className="p-4 rounded-tl-lg text-sm font-semibold">Floor</th>
                      <th className="p-4 text-sm font-semibold">Area (Sq.Ft)</th>
                      <th className="p-4 text-sm font-semibold">Rate/Sq.Ft</th>
                      <th className="p-4 text-sm font-semibold">Total Amount</th>
                      <th className="p-4 text-sm font-semibold">Down Payment (25%)</th>
                      <th className="p-4 text-sm font-semibold text-yellow-300" colSpan="4">4 Quarterly Installments (18.75% Each)</th>
                    </tr>
                    <tr className="bg-blue-800">
                      <th className="p-2" colSpan="5"></th>
                      <th className="p-2 text-xs text-center">Q1</th>
                      <th className="p-2 text-xs text-center">Q2</th>
                      <th className="p-2 text-xs text-center">Q3</th>
                      <th className="p-2 text-xs text-center rounded-tr-lg">Q4</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paymentPlans.map((plan, index) => (
                      <tr key={index} className={`hover:bg-blue-50 transition ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                        <td className="p-4 font-medium text-slate-700">{plan.floor}</td>
                        <td className="p-4 text-slate-600">{plan.area.toLocaleString()}</td>
                        <td className="p-4 text-slate-600">{plan.rate.toLocaleString()}</td>
                        <td className="p-4 font-bold text-blue-900">{formatCurrency(plan.total)}</td>
                        <td className="p-4 font-semibold text-green-600">{formatCurrency(plan.down)}</td>
                        <td className="p-4 text-slate-600 text-center">{formatCurrency(plan.q1)}</td>
                        <td className="p-4 text-slate-600 text-center">{formatCurrency(plan.q2)}</td>
                        <td className="p-4 text-slate-600 text-center">{formatCurrency(plan.q3)}</td>
                        <td className="p-4 text-slate-600 text-center">{formatCurrency(plan.q4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="p-6 border-t border-gray-200 bg-slate-50 flex justify-between items-center">
                <div className="flex gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><ShieldCheck size={16} className="text-green-500"/> Safe & Secure Investment</span>
                  <span className="flex items-center gap-1"><TrendingUp size={16} className="text-blue-500"/> Premium Commercial Asset</span>
                  <span className="flex items-center gap-1"><CheckCircle size={16} className="text-yellow-500"/> Flexible Installments</span>
                </div>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* --- NEW CAROUSEL SECTION (ABOVE FOOTER) --- */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-800 mb-4">Floor Plan <span className="text-blue-600">Gallery</span></h2>
          <p className="text-center text-gray-500 mb-10">Explore the detailed layout of B7 Tower 1</p>
          
          <div className="relative group w-full max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl bg-white">
            {/* Main Image */}
            <div className="relative aspect-[4/3] md:aspect-[16/9] w-full bg-gray-100 flex items-center justify-center">
              <img 
                src={carouselImages[currentIndex]} 
                alt={`Floor Plan ${currentIndex + 1}`} 
                className="w-full h-full object-contain p-4"
              />
              
              {/* Navigation Arrows */}
              <button 
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg text-slate-800 hover:text-blue-600 transition z-10 opacity-0 group-hover:opacity-100"
                aria-label="Previous image"
              >
                <ChevronLeft size={28} />
              </button>
              <button 
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg text-slate-800 hover:text-blue-600 transition z-10 opacity-0 group-hover:opacity-100"
                aria-label="Next image"
              >
                <ChevronRight size={28} />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10 bg-black/40 px-4 py-2 rounded-full backdrop-blur-sm">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentIndex === index 
                      ? 'bg-yellow-500 scale-125' 
                      : 'bg-white/60 hover:bg-white'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* --- FOOTER --- */}
        <footer className="bg-blue-950 text-white py-8 mt-12">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h3 className="text-2xl font-bold tracking-wider mb-2">B7 <span className="text-yellow-500">TOWER 1</span></h3>
            <p className="text-blue-200 text-sm">Plot # 112, Jinnah Avenue, Sector F7/G7, Blue Area, Islamabad</p>
            <div className="flex justify-center gap-6 mt-6 text-xs text-blue-300">
              <span>Marketing & Developers</span>
              <span>|</span>
              <span>From Vision to Value</span>
            </div>
            <div className="mt-6 border-t border-blue-800 pt-4 text-xs text-blue-400">
              &copy; 2026 B7 Tower 1. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </HelmetProvider>
  );
};

export default B7TowerMarketing;